import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { formatDistanceToNow, parseISO } from "date-fns";
import { el } from "date-fns/locale";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  Gauge,
  Info,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  SlidersHorizontal,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import {
  fetchAssignmentMetrics,
  type AssignmentPartnerMetric,
  type AssignmentRange,
  type AssignmentService,
} from "@/api/adminAssignments";
import { AdminErrorState, AdminMetricSkeletons } from "@/components/admin/AdminDataState";
import { SERVICE_OPTIONS as CATALOG_SERVICE_OPTIONS } from "@/lib/serviceCatalog";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/StatCard";
import { cn } from "@/lib/utils";

const formatNumber = new Intl.NumberFormat("el-GR").format;
const PAGE_SIZE = 12;

const RANGE_OPTIONS: Array<{ value: AssignmentRange; label: string }> = [
  { value: "7d", label: "7 ημέρες" },
  { value: "30d", label: "30 ημέρες" },
  { value: "90d", label: "90 ημέρες" },
  { value: "1y", label: "12 μήνες" },
  { value: "all", label: "Όλο το ιστορικό" },
];

const SERVICE_OPTIONS: Array<{ value: AssignmentService; label: string }> = [
  { value: "all", label: "Όλες οι υπηρεσίες" },
  ...CATALOG_SERVICE_OPTIONS.map(({ value, label }) => ({ value, label })),
];

type PartnerSort = "volume" | "success" | "view" | "open" | "recent" | "name";
type PartnerState = "all" | "active" | "with_leads" | "zero";

const SORT_OPTIONS: Array<{ value: PartnerSort; label: string }> = [
  { value: "volume", label: "Περισσότερα leads" },
  { value: "success", label: "Υψηλότερη επιτυχία" },
  { value: "view", label: "Υψηλότερη προβολή" },
  { value: "open", label: "Περισσότερα μη κλεισμένα" },
  { value: "recent", label: "Πιο πρόσφατη ανάθεση" },
  { value: "name", label: "Όνομα Α–Ω" },
];

const PARTNER_STATE_OPTIONS: Array<{ value: PartnerState; label: string }> = [
  { value: "all", label: "Όλοι οι συνεργάτες" },
  { value: "active", label: "Μόνο ενεργοί" },
  { value: "with_leads", label: "Με leads" },
  { value: "zero", label: "Χωρίς lead στην περίοδο" },
];

const SERVICE_LABELS = Object.fromEntries(SERVICE_OPTIONS.map((option) => [option.value, option.label]));

const percent = (value: number, total: number): number => total > 0 ? Math.round((value / total) * 100) : 0;
const openLeads = (partner: AssignmentPartnerMetric): number => Math.max(0, partner.total - partner.success);
const partnerSuccessRate = (partner: AssignmentPartnerMetric): number => percent(partner.success, partner.total);
const partnerViewRate = (partner: AssignmentPartnerMetric): number => percent(partner.viewed, partner.total);

const relativeDate = (value: string | null): string => {
  if (!value) return "Καμία ανάθεση";
  try {
    return formatDistanceToNow(parseISO(value.replace(" ", "T")), { addSuffix: true, locale: el });
  } catch {
    return value;
  }
};

const AssignmentStats: React.FC = () => {
  const [range, setRange] = useState<AssignmentRange>("30d");
  const [service, setService] = useState<AssignmentService>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<PartnerSort>("volume");
  const [partnerState, setPartnerState] = useState<PartnerState>("all");
  const [page, setPage] = useState(1);

  const metricsQuery = useQuery({
    queryKey: ["admin-assignment-metrics", range, service],
    queryFn: () => fetchAssignmentMetrics(range, service),
    staleTime: 45_000,
    placeholderData: keepPreviousData,
  });

  const data = metricsQuery.data;
  const summary = data?.summary ?? {
    total: 0,
    assigned: 0,
    unassigned: 0,
    new_leads: 0,
    pending: 0,
    information: 0,
    success: 0,
    assigned_success: 0,
    viewed: 0,
  };

  const coverageRate = percent(summary.assigned, summary.total);
  const successRate = percent(summary.assigned_success, summary.assigned);
  const viewRate = percent(summary.viewed, summary.assigned);
  const activeEligible = (data?.partners ?? []).filter((partner) => partner.active).length;
  const activeWithLeads = (data?.partners ?? []).filter((partner) => partner.active && partner.total > 0).length;
  const zeroLoadPartners = (data?.partners ?? []).filter((partner) => partner.active && partner.total === 0).length;
  const topPartners = useMemo(
    () => [...(data?.partners ?? [])].filter((partner) => partner.total > 0).sort((a, b) => b.total - a.total).slice(0, 8),
    [data?.partners],
  );
  const maxPartnerTotal = Math.max(1, ...topPartners.map((partner) => partner.total));
  const topPartnerShare = topPartners[0] ? percent(topPartners[0].total, summary.assigned) : 0;

  const filteredPartners = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("el-GR");
    const rows = (data?.partners ?? []).filter((partner) => {
      const matchesSearch = !normalizedSearch || `${partner.name} ${partner.location}`.toLocaleLowerCase("el-GR").includes(normalizedSearch);
      const matchesState =
        partnerState === "all" ||
        (partnerState === "active" && partner.active) ||
        (partnerState === "with_leads" && partner.total > 0) ||
        (partnerState === "zero" && partner.active && partner.total === 0);
      return matchesSearch && matchesState;
    });

    return rows.sort((a, b) => {
      if (sort === "success") return partnerSuccessRate(b) - partnerSuccessRate(a) || b.total - a.total;
      if (sort === "view") return partnerViewRate(b) - partnerViewRate(a) || b.total - a.total;
      if (sort === "open") return openLeads(b) - openLeads(a) || b.total - a.total;
      if (sort === "recent") return (b.last_lead || "").localeCompare(a.last_lead || "") || a.name.localeCompare(b.name, "el");
      if (sort === "name") return a.name.localeCompare(b.name, "el");
      return b.total - a.total || a.name.localeCompare(b.name, "el");
    });
  }, [data?.partners, partnerState, search, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredPartners.length / PAGE_SIZE));
  const visiblePartners = filteredPartners.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => setPage(1), [partnerState, search, sort, range, service]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const resetFilters = () => {
    setRange("30d");
    setService("all");
    setSearch("");
    setSort("volume");
    setPartnerState("all");
  };
  const hasFilters = range !== "30d" || service !== "all" || search.trim() !== "" || sort !== "volume" || partnerState !== "all";

  const pipelineRows = [
    { label: "Νέα", value: summary.new_leads, tone: "bg-sky-500", text: "text-sky-700" },
    { label: "Σε εκκρεμότητα", value: summary.pending, tone: "bg-amber-500", text: "text-amber-700" },
    { label: "Ζήτησαν πληροφορίες", value: summary.information, tone: "bg-violet-500", text: "text-violet-700" },
    { label: "Έκλεισε η δουλειά", value: summary.success, tone: "bg-emerald-500", text: "text-emerald-700" },
  ];

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        eyebrow="Καθημερινή εργασία"
        title="Κατανομή αναθέσεων"
        description="Η τρέχουσα κατανομή των leads που δημιουργήθηκαν στην περίοδο, τα κενά κάλυψης και η εξέλιξή τους — χωρίς αυτόματες μαζικές αλλαγές."
        icon={<BarChart3 />}
        iconGradient="blue"
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button asChild variant="outline" className="h-11 w-full gap-2 sm:w-auto">
              <Link to="/admin/operations"><AlertTriangle className="h-4 w-4" /> Operations Inbox</Link>
            </Button>
            <Button type="button" variant="outline" className="h-11 w-full gap-2 sm:w-auto" onClick={() => void metricsQuery.refetch()} disabled={metricsQuery.isFetching}>
              <RefreshCw className={cn("h-4 w-4", metricsQuery.isFetching && "animate-spin")} /> Ανανέωση
            </Button>
          </div>
        }
      />

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <p className="flex items-center gap-2 text-sm font-semibold text-slate-950"><SlidersHorizontal className="h-4 w-4 text-sky-700" /> Φίλτρα κατανομής</p>
              <p className="mt-1 text-xs leading-5 text-slate-500">Όλα τα KPI και οι συνεργάτες ακολουθούν την ίδια περίοδο και υπηρεσία.</p>
              <p className="text-[11px] leading-5 text-slate-400">Η περίοδος αφορά την ημερομηνία δημιουργίας του lead.</p>
            </div>
            {hasFilters && (
              <Button type="button" variant="ghost" size="sm" onClick={resetFilters} className="h-11 shrink-0 gap-1.5 text-slate-600">
                <X className="h-4 w-4" /> <span className="hidden sm:inline">Καθαρισμός</span>
              </Button>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[170px_220px_minmax(220px,1fr)_190px_210px]">
            <Select value={range} onValueChange={(value) => setRange(value as AssignmentRange)}>
              <SelectTrigger className="h-11" aria-label="Περίοδος"><SelectValue /></SelectTrigger>
              <SelectContent>{RANGE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={service} onValueChange={(value) => setService(value as AssignmentService)}>
              <SelectTrigger className="h-11" aria-label="Υπηρεσία"><SelectValue /></SelectTrigger>
              <SelectContent>{SERVICE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="relative sm:col-span-2 xl:col-span-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Αναζήτηση συνεργάτη ή περιοχής" aria-label="Αναζήτηση συνεργάτη" className="h-11 pl-9" />
            </div>
            <Select value={partnerState} onValueChange={(value) => setPartnerState(value as PartnerState)}>
              <SelectTrigger className="h-11" aria-label="Κατάσταση συνεργάτη"><SelectValue /></SelectTrigger>
              <SelectContent>{PARTNER_STATE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={sort} onValueChange={(value) => setSort(value as PartnerSort)}>
              <SelectTrigger className="h-11" aria-label="Ταξινόμηση"><SelectValue /></SelectTrigger>
              <SelectContent>{SORT_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {metricsQuery.isLoading ? (
        <>
          <AdminMetricSkeletons count={4} />
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.4fr)_minmax(320px,0.6fr)]">
            <Skeleton className="h-96 rounded-2xl" />
            <Skeleton className="h-96 rounded-2xl" />
          </div>
        </>
      ) : metricsQuery.isError ? (
        <AdminErrorState message={metricsQuery.error instanceof Error ? metricsQuery.error.message : "Δεν φορτώθηκαν τα στοιχεία αναθέσεων."} onRetry={() => void metricsQuery.refetch()} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard title="Κάλυψη ανάθεσης" value={`${coverageRate}%`} description={`${formatNumber(summary.assigned)} από ${formatNumber(summary.total)} leads`} icon={<UserCheck />} variant={coverageRate >= 95 ? "success" : "warning"} size="compact" />
            <StatCard title="Χωρίς συνεργάτη" value={formatNumber(summary.unassigned)} description={summary.unassigned > 0 ? "Χρειάζονται χειροκίνητο έλεγχο" : "Καμία εκκρεμότητα περιόδου"} icon={<AlertTriangle />} variant={summary.unassigned > 0 ? "danger" : "success"} size="compact" />
            <StatCard title="Επιτυχία" value={`${successRate}%`} description={`${formatNumber(summary.assigned_success)} κλεισμένες με συνεργάτη`} icon={<CheckCircle2 />} variant="success" size="compact" />
            <StatCard title="Προβολή lead" value={`${viewRate}%`} description={`${formatNumber(summary.viewed)} assigned leads ανοίχτηκαν`} icon={<Eye />} variant="info" size="compact" />
          </div>

          {summary.unassigned > 0 && (
            <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
                <div>
                  <p className="text-sm font-semibold text-amber-950">{formatNumber(summary.unassigned)} leads δεν έχουν συνεργάτη σε αυτό το φίλτρο</p>
                  <p className="mt-0.5 text-xs leading-5 text-amber-800">Παραμένουν ορατά και δεν ανατίθενται εκτός ακτίνας χωρίς χειροκίνητη απόφαση.</p>
                </div>
              </div>
              <Button asChild className="h-11 shrink-0 bg-amber-900 text-white hover:bg-amber-800">
                <Link to="/admin/operations">Έλεγχος στο Operations <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          )}

          <div className="grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
            <Card className="min-w-0 rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <CardTitle className="text-base text-slate-950">Φορτίο ανά συνεργάτη</CardTitle>
                    <CardDescription className="mt-1">Οι 8 μεγαλύτερες κατανομές στο επιλεγμένο φίλτρο.</CardDescription>
                  </div>
                  <Badge variant="outline" className="w-fit border-slate-200 bg-slate-50 text-slate-600">{activeWithLeads}/{activeEligible} ενεργοί με lead</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {topPartners.length > 0 ? topPartners.map((partner, index) => (
                  <div key={partner.id} className="group">
                    <div className="mb-1.5 flex items-center justify-between gap-3 text-sm">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[11px] font-bold text-slate-500">{index + 1}</span>
                        <Link to={`/admin/partners/${partner.id}/dashboard`} className="truncate font-semibold text-slate-800 hover:text-sky-700 hover:underline">{partner.name}</Link>
                      </div>
                      <span className="shrink-0 font-semibold tabular-nums text-slate-900">{formatNumber(partner.total)} <span className="font-normal text-slate-400">· {percent(partner.total, summary.assigned)}%</span></span>
                    </div>
                    <div className="ml-8 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-sky-600 transition-all" style={{ width: `${Math.max(2, (partner.total / maxPartnerTotal) * 100)}%` }} />
                    </div>
                  </div>
                )) : (
                  <div className="flex min-h-48 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 px-6 text-center">
                    <Users className="h-6 w-6 text-slate-300" />
                    <p className="mt-3 text-sm font-semibold text-slate-700">Δεν υπάρχουν αναθέσεις</p>
                    <p className="mt-1 text-xs leading-5 text-slate-500">Αλλάξτε περίοδο ή υπηρεσία για να δείτε άλλη κατανομή.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="min-w-0 rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-slate-950">Λειτουργική εικόνα</CardTitle>
                <CardDescription className="mt-1">Signals που εξηγούν την κατανομή, όχι αυτόματες αποφάσεις.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Μερίδιο πρώτου συνεργάτη</p>
                      <p className="mt-1.5 text-3xl font-bold tabular-nums tracking-tight text-slate-950">{topPartnerShare}%</p>
                    </div>
                    <Gauge className="h-7 w-7 text-sky-700" />
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-sky-600" style={{ width: `${topPartnerShare}%` }} /></div>
                  <p className="mt-2 text-xs leading-5 text-slate-500">Δεν αποτελεί μόνο του ένδειξη αδικίας: υπηρεσία, περιοχή και ακτίνα αλλάζουν το διαθέσιμο δίκτυο.</p>
                </div>

                <div className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-5">
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="text-xs text-slate-500">Ενεργοί επιλέξιμοι</p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{activeEligible}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <p className="text-xs text-slate-500">Χωρίς lead</p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-slate-950">{zeroLoadPartners}</p>
                  </div>
                </div>

                <div className="border-t border-slate-100 pt-5">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">Κατάσταση leads περιόδου</p>
                  <div className="space-y-3">
                    {pipelineRows.map((row) => (
                      <div key={row.label}>
                        <div className="mb-1 flex items-center justify-between gap-3 text-xs"><span className="text-slate-600">{row.label}</span><span className={cn("font-semibold tabular-nums", row.text)}>{formatNumber(row.value)} · {percent(row.value, summary.total)}%</span></div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-slate-100"><div className={cn("h-full rounded-full", row.tone)} style={{ width: `${percent(row.value, summary.total)}%` }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {service === "all" && (data?.services.length ?? 0) > 0 && (
            <Card className="rounded-2xl border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-slate-950">Κάλυψη ανά υπηρεσία</CardTitle>
                <CardDescription className="mt-1">Επιλέξτε υπηρεσία για να περιορίσετε όλο το workspace.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {data?.services.map((row) => {
                  const rowCoverage = percent(row.assigned, row.total);
                  return (
                    <button key={row.service} type="button" onClick={() => setService(row.service as AssignmentService)} className="min-h-28 rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-sky-300 hover:bg-sky-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-900">{SERVICE_LABELS[row.service] || row.service}</p>
                        <span className={cn("text-sm font-bold tabular-nums", rowCoverage >= 95 ? "text-emerald-700" : "text-amber-700")}>{rowCoverage}%</span>
                      </div>
                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100"><div className={cn("h-full rounded-full", rowCoverage >= 95 ? "bg-emerald-500" : "bg-amber-500")} style={{ width: `${rowCoverage}%` }} /></div>
                      <p className="mt-2 text-xs text-slate-500">{formatNumber(row.assigned)} assigned · {formatNumber(row.unassigned)} χωρίς συνεργάτη</p>
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          )}

          <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100 bg-slate-50/60 pb-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <CardTitle className="text-base text-slate-950">Συνεργάτες στο φίλτρο</CardTitle>
                  <CardDescription className="mt-1">{formatNumber(filteredPartners.length)} αποτελέσματα · πραγματικά aggregated στοιχεία χωρίς contact data.</CardDescription>
                </div>
                {metricsQuery.isFetching && <span className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Ανανέωση δεδομένων</span>}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {visiblePartners.length === 0 ? (
                <div className="flex min-h-64 flex-col items-center justify-center px-6 py-10 text-center">
                  <Search className="h-6 w-6 text-slate-300" />
                  <p className="mt-3 text-sm font-semibold text-slate-800">Δεν βρέθηκε συνεργάτης</p>
                  <p className="mt-1 max-w-sm text-xs leading-5 text-slate-500">Δοκιμάστε διαφορετική αναζήτηση, υπηρεσία ή κατάσταση.</p>
                  {hasFilters && <Button type="button" variant="outline" onClick={resetFilters} className="mt-4 h-11">Καθαρισμός φίλτρων</Button>}
                </div>
              ) : (
                <>
                  <div className="divide-y divide-slate-100 md:hidden">
                    {visiblePartners.map((partner) => (
                      <article key={partner.id} className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <Link to={`/admin/partners/${partner.id}/dashboard`} className="text-sm font-semibold text-slate-900 hover:text-sky-700 hover:underline">{partner.name}</Link>
                            <p className="mt-1 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{partner.location || "Χωρίς περιοχή"}</span></p>
                          </div>
                          <Badge variant="outline" className={cn("shrink-0", partner.active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-600")}>{partner.active ? "Ενεργός" : "Ανενεργός"}</Badge>
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-2">
                          <div className="rounded-xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-wide text-slate-400">Leads</p><p className="mt-1 text-lg font-bold tabular-nums text-slate-900">{partner.total}</p></div>
                          <div className="rounded-xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-wide text-slate-400">Επιτυχία</p><p className="mt-1 text-lg font-bold tabular-nums text-emerald-700">{partnerSuccessRate(partner)}%</p></div>
                          <div className="rounded-xl bg-slate-50 p-2.5"><p className="text-[10px] uppercase tracking-wide text-slate-400">Προβολή</p><p className="mt-1 text-lg font-bold tabular-nums text-sky-700">{partnerViewRate(partner)}%</p></div>
                        </div>
                        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500"><span>{openLeads(partner)} μη κλεισμένα</span><span>{relativeDate(partner.last_lead)}</span></div>
                        <Button asChild variant="outline" className="mt-3 h-11 w-full"><Link to={`/admin/partners/${partner.id}/dashboard`}>Άνοιγμα συνεργάτη <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
                      </article>
                    ))}
                  </div>

                  <div className="hidden overflow-x-auto md:block">
                    <table className="w-full min-w-[900px] text-left text-sm">
                      <thead className="border-b border-slate-200 bg-white text-xs uppercase tracking-[0.06em] text-slate-500">
                        <tr>
                          <th className="px-5 py-3.5 font-semibold">Συνεργάτης</th>
                          <th className="px-4 py-3.5 text-right font-semibold">Leads</th>
                          <th className="px-4 py-3.5 text-right font-semibold">Μη κλεισμένα</th>
                          <th className="px-4 py-3.5 text-right font-semibold">Επιτυχία</th>
                          <th className="px-4 py-3.5 text-right font-semibold">Προβολή</th>
                          <th className="px-4 py-3.5 font-semibold">Τελευταίο lead</th>
                          <th className="px-5 py-3.5 text-right font-semibold">Ενέργεια</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {visiblePartners.map((partner) => (
                          <tr key={partner.id} className="transition hover:bg-slate-50/80">
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <span className={cn("h-2 w-2 shrink-0 rounded-full", partner.active ? "bg-emerald-500" : "bg-slate-300")} />
                                <div className="min-w-0"><p className="max-w-64 truncate font-semibold text-slate-900">{partner.name}</p><p className="mt-0.5 max-w-64 truncate text-xs text-slate-500">{partner.location || "Χωρίς περιοχή"}</p></div>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-right font-semibold tabular-nums text-slate-900">{partner.total}</td>
                            <td className="px-4 py-4 text-right tabular-nums text-amber-700">{openLeads(partner)}</td>
                            <td className="px-4 py-4 text-right"><span className="font-semibold tabular-nums text-emerald-700">{partnerSuccessRate(partner)}%</span><span className="ml-1 text-xs text-slate-400">({partner.success})</span></td>
                            <td className="px-4 py-4 text-right font-semibold tabular-nums text-sky-700">{partnerViewRate(partner)}%</td>
                            <td className="px-4 py-4 text-xs text-slate-500">{relativeDate(partner.last_lead)}</td>
                            <td className="px-5 py-4 text-right"><Button asChild variant="ghost" size="sm" className="h-10"><Link to={`/admin/partners/${partner.id}/dashboard`}>Προβολή <ArrowRight className="ml-1.5 h-4 w-4" /></Link></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {filteredPartners.length > 0 && (
                <div className="flex flex-col gap-3 border-t border-slate-100 bg-slate-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                  <p className="text-xs text-slate-500">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredPartners.length)} από {filteredPartners.length}</p>
                  <div className="flex items-center justify-between gap-2 sm:justify-end">
                    <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1} aria-label="Προηγούμενη σελίδα"><ChevronLeft className="h-4 w-4" /></Button>
                    <span className="min-w-24 text-center text-xs font-medium text-slate-600">Σελίδα {page} / {totalPages}</span>
                    <Button type="button" variant="outline" size="icon" className="h-11 w-11" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages} aria-label="Επόμενη σελίδα"><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="rounded-2xl border border-sky-100 bg-sky-50/70 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-700" />
              <div>
                <p className="text-sm font-semibold text-sky-950">Πώς διαβάζουμε την κατανομή</p>
                <p className="mt-1 text-xs leading-5 text-sky-900">Η αυτόματη επιλογή χρησιμοποιεί συμβατότητα υπηρεσίας, γεωγραφική κάλυψη και υφιστάμενο πλήθος leads. Η σελίδα είναι μόνο για παρακολούθηση· δεν εκτελεί bulk reassignment. Οι χειροκίνητες εξαιρέσεις γίνονται από το Operations Inbox με ρητή επιλογή συνεργάτη.</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AssignmentStats;
